import { useState, useEffect } from 'react'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import { getTeacherClasses } from '../services/teacherClassService'
import {
  createAssignment,
  getAssignmentsByClass,
  ASSIGNMENTS_BY_CLASS_UI_LIMIT,
  filterAssignmentsContainingExercise
} from '../services/assignmentService'
import { resolveExerciseReference } from '../services/exerciseService'
import { getExerciseDisplayTitle } from '../utils/exerciseDisplay'
import './AssignToClassModal.css'

function getYouTubeThumbnail(videoId) {
  if (!videoId) return null
  const id = videoId.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/)?.[1] || videoId
  return `https://img.youtube.com/vi/${id}/mqdefault.jpg`
}

function AssignToClassModal({ isOpen, onClose, exercise, teacherId, onSuccess }) {
  const [teacherClasses, setTeacherClasses] = useState([])
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [selectedClassId, setSelectedClassId] = useState('')
  const [title, setTitle] = useState('')
  const [instructions, setInstructions] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [extraExerciseRefs, setExtraExerciseRefs] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [checkingClassAssignments, setCheckingClassAssignments] = useState(false)
  const [classAssignmentHint, setClassAssignmentHint] = useState(null)

  useEffect(() => {
    if (!isOpen || !teacherId) return
    setLoadingClasses(true)
    setError('')
    setSelectedClassId('')
    setTitle('')
    setInstructions('')
    setDueDate('')
    setExtraExerciseRefs('')
    setClassAssignmentHint(null)
    getTeacherClasses(teacherId)
      .then(setTeacherClasses)
      .catch(() => setTeacherClasses([]))
      .finally(() => setLoadingClasses(false))
  }, [isOpen, teacherId])

  useEffect(() => {
    if (!isOpen || !selectedClassId || !exercise?.id) {
      setClassAssignmentHint(null)
      setCheckingClassAssignments(false)
      return
    }
    let cancelled = false
    setCheckingClassAssignments(true)
    setClassAssignmentHint(null)
    getAssignmentsByClass(selectedClassId, ASSIGNMENTS_BY_CLASS_UI_LIMIT)
      .then((list) => {
        if (cancelled) return
        const matches = filterAssignmentsContainingExercise(list, exercise.id)
        const truncated = list.length >= ASSIGNMENTS_BY_CLASS_UI_LIMIT
        let latestTs = null
        matches.forEach((a) => {
          const ts = a.createdAt?.toDate?.() ?? a.createdAt
          const d = ts instanceof Date ? ts : ts ? new Date(ts) : null
          if (d && !Number.isNaN(d.getTime()) && (!latestTs || d > latestTs)) latestTs = d
        })
        const className =
          teacherClasses.find((c) => c.id === selectedClassId)?.name || 'cette classe'
        setClassAssignmentHint({
          count: matches.length,
          latestDate: latestTs,
          className,
          truncated
        })
      })
      .catch(() => {
        if (!cancelled) setClassAssignmentHint(null)
      })
      .finally(() => {
        if (!cancelled) setCheckingClassAssignments(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, selectedClassId, exercise?.id, teacherClasses])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!selectedClassId || !exercise?.id || !teacherId) return
    setSubmitting(true)
    try {
      const baseItem = {
        exerciseId: exercise.id,
        exerciseTitle: getExerciseDisplayTitle(exercise, []) || exercise.video?.title || 'Exercice',
        exerciseThumbnail: exercise.thumbnailUrl || getYouTubeThumbnail(exercise.video?.id) || null
      }

      const refs = extraExerciseRefs
        .split(/[\n,;]+/)
        .map((value) => value.trim())
        .filter(Boolean)

      const resolvedExtras = []
      for (const ref of refs) {
        const resolved = await resolveExerciseReference(ref)
        if (!resolved?.id) {
          throw new Error(`Référence d'exercice introuvable : ${ref}`)
        }
        resolvedExtras.push({
          exerciseId: resolved.id,
          exerciseTitle: getExerciseDisplayTitle(resolved, []) || resolved.video?.title || 'Exercice',
          exerciseThumbnail: resolved.thumbnailUrl || getYouTubeThumbnail(resolved.video?.id) || null
        })
      }

      const dedupItems = new Map()
      ;[baseItem, ...resolvedExtras].forEach((item) => {
        if (!dedupItems.has(item.exerciseId)) dedupItems.set(item.exerciseId, item)
      })
      const items = Array.from(dedupItems.values())

      await createAssignment(teacherId, selectedClassId, items, {
        title: title.trim() || null,
        instructions: instructions.trim() || null,
        dueDate: dueDate ? new Date(dueDate + 'T23:59:59') : null,
        items
      })
      onSuccess?.({ itemCount: items.length })
      onClose()
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'assignation.')
    } finally {
      setSubmitting(false)
    }
  }

  const backdropDismiss = useBackdropDismiss(onClose)

  if (!isOpen) return null

  return (
    <div className="assign-to-class-modal-backdrop" {...backdropDismiss}>
      <div className="assign-to-class-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="assign-to-class-modal-header">
          <h2>Assigner à une classe</h2>
          <button type="button" className="assign-to-class-modal-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <p className="assign-to-class-modal-exercise">
          {exercise ? (getExerciseDisplayTitle(exercise, []) || exercise.video?.title || 'Exercice') : 'Exercice'}
        </p>
        <form onSubmit={handleSubmit} className="assign-to-class-modal-form">
          <label>
            Classe <span className="required">*</span>
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              required
              disabled={loadingClasses}
            >
              <option value="">— Choisir —</option>
              {teacherClasses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          {selectedClassId && checkingClassAssignments && (
            <p className="assign-to-class-modal-hint" role="status">Vérification des devoirs existants…</p>
          )}
          {selectedClassId && !checkingClassAssignments && classAssignmentHint && classAssignmentHint.count > 0 && (
            <div className="assign-to-class-modal-existing" role="status">
              <p>
                Déjà assigné à {classAssignmentHint.className} — {classAssignmentHint.count} devoir
                {classAssignmentHint.count > 1 ? 's' : ''} {classAssignmentHint.count > 1 ? 'contiennent' : 'contient'} cet exercice.
                {classAssignmentHint.latestDate && (
                  <>
                    {' '}
                    Dernier :{' '}
                    {new Intl.DateTimeFormat('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }).format(classAssignmentHint.latestDate)}
                    .
                  </>
                )}
              </p>
              {classAssignmentHint.truncated && (
                <p className="assign-to-class-modal-existing-truncate">
                  Liste limitée aux {ASSIGNMENTS_BY_CLASS_UI_LIMIT} devoirs les plus récents : le total réel peut être plus élevé.
                </p>
              )}
            </div>
          )}
          {teacherClasses.length === 0 && !loadingClasses && (
            <p className="assign-to-class-modal-hint">Créez d’abord une classe dans Mes classes.</p>
          )}
          <label>
            Titre du devoir (optionnel)
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex. Devoir 1 – Cadences"
            />
          </label>
          <label>
            Consigne (optionnel)
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="ex. Analyse complète puis comparez les cadences."
              rows={3}
            />
          </label>
          <label>
            Date limite (optionnel)
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>
          <label>
            Exercices supplémentaires (optionnel)
            <textarea
              value={extraExerciseRefs}
              onChange={(e) => setExtraExerciseRefs(e.target.value)}
              placeholder="Un code court ou ID par ligne (ou séparés par virgules)"
              rows={3}
            />
          </label>
          {error && <p className="assign-to-class-modal-error">{error}</p>}
          <div className="assign-to-class-modal-actions">
            <button type="button" onClick={onClose} disabled={submitting}>Annuler</button>
            <button type="submit" disabled={submitting || !selectedClassId || loadingClasses}>
              {submitting ? 'Envoi…' : 'Assigner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssignToClassModal
